/**
 * @file useShopsList.ts
 * @description Данные экрана «Магазины» (phase19.28 этап А;
 * uiux-platform-shops §2, §7): поиск на сервере (`?q=`, с паузой 300 мс),
 * отбор-пилюли на клиенте, страницы по 25 и журнал неизвестных адресов.
 */

import { useMemo, useState } from "react";

import { platformShops, type ShopListItem, type UnknownDomain } from "../../lib/api-platform-shops";
import { filterShops, shopFilterChips, type ShopFilter } from "../../lib/platform/shop-list-filter";
import { useDebounced } from "./useDebounced";
import { usePlatformResource } from "./usePlatformResource";

const SEARCH_DELAY_MS = 300;
export const SHOPS_PAGE_SIZE = 25;

const LIST_ERROR = "Не удалось загрузить магазины. Проверьте соединение и повторите";

export function useShopsList() {
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<ShopFilter>("all");
  const [page, setPage] = useState(0);
  const q = useDebounced(search.trim(), SEARCH_DELAY_MS);

  const list = usePlatformResource<ShopListItem[]>(
    `shops?q=${q}`,
    () => platformShops.list(q),
    LIST_ERROR,
  );
  const unknown = usePlatformResource<UnknownDomain[]>(
    "domains/unknown",
    () => platformShops.unknownDomains(7),
    "Не удалось загрузить журнал неизвестных адресов",
  );

  const items = useMemo(() => list.data ?? [], [list.data]);
  const chips = useMemo(() => shopFilterChips(items), [items]);
  // Отбор, которого больше нет среди пилюль (пропали данные проверок), — сбрасываем на «Все».
  const activeFilter: ShopFilter = chips.some((chip) => chip.value === filter) ? filter : "all";
  const rows = useMemo(() => filterShops(items, activeFilter), [items, activeFilter]);
  const pageRows = rows.slice(page * SHOPS_PAGE_SIZE, (page + 1) * SHOPS_PAGE_SIZE);

  return {
    search,
    setSearch: (value: string) => {
      setSearch(value);
      setPage(0);
    },
    filter: activeFilter,
    setFilter: (value: ShopFilter) => {
      setFilter(value);
      setPage(0);
    },
    chips,
    list,
    items,
    rows,
    pageRows,
    page,
    setPage,
    /** Пусто из-за поиска или отбора, а не потому что магазинов нет. */
    filtered: q !== "" || activeFilter !== "all",
    unknown,
  };
}
